/**
 * The metrics page's engine section (TRA-1484): what vLLM on Baseten
 * reports about a dedicated-fleet model, as the gateway samples it every
 * 2 minutes. GPU usage, KV cache usage, requests running and waiting, and
 * speculative decoding, in the same cards and chart style as the gateway
 * metrics above. Its own query, so a failed read leaves the rest of the
 * page whole. The cards draw the stretch the page shows (TRA-1592): a
 * zoom or a move cuts the loaded samples, and only a longer window reads.
 */

import { SectionHeading } from '@/components/models/home/ModelHomeSection'
import { Note } from '@/components/models/home/PanControls'
import type { TimeRange } from '@/components/models/home/replicaZoom'
import { formatRate } from '@/components/models/home/contracts'
import {
  extent,
  formatCountTick,
  formatPercent,
  formatPercentTick,
} from '@/components/models/metrics/contracts'
import type { EngineBucket, EngineMetric, EngineView } from '@/components/models/metrics/engineMetrics'
import {
  NO_ENGINE_DATA_YET,
  formatCount,
  hasSamples,
  isMissing,
  missingNote,
  toEngineView,
} from '@/components/models/metrics/engineMetrics'
import {
  BucketLineChart,
  CHART_1,
  CHART_2,
  Caption,
  type DragZoom,
  Frame,
  Headline,
  Legend,
  LineMark,
  SeriesLine,
  ValueAxis,
  type LegendItem,
} from '@/components/models/metrics/MetricsCharts'
import { engineHoursToLoad } from '@/components/models/metrics/metricsPan'
import { Empty, MetricsCard, SideBySide } from '@/components/models/metrics/ModelMetricsPanel'
import { Skeleton } from '@/components/ui/skeleton'
import { useModelEngineMetrics } from '@/hooks/useModels'

const NOT_ON_FLEET = 'This model has no TrainLoop deployment, so there are no engine metrics to show.'
const ROUTE_FAILED = "Failed to load the model's route."
const LOAD_FAILED = 'Failed to load engine metrics.'
const NO_SAMPLES = 'No engine samples in this window.'

interface Card {
  title: string
  subtitle: string
  /** The card takes the full row: a wide chart, or two charts side by side. */
  wide: boolean
}

const CARDS: Card[] = [
  { title: 'GPU usage', subtitle: 'Average and highest GPU utilization across replicas', wide: false },
  { title: 'KV cache usage', subtitle: 'GPU memory holding the text of running requests', wide: false },
  {
    title: 'Requests running and waiting',
    subtitle:
      'Requests inside vLLM at each 2-minute sample. The Concurrent requests card is the exact count from gateway traffic.',
    wide: true,
  },
  {
    title: 'Spec decode',
    subtitle: 'How many draft tokens the model accepted and how fast it emitted tokens',
    wide: true,
  },
]

interface Series {
  key: EngineMetric
  /** The legend entry. */
  label: string
  /** The words a note uses when Baseten does not send the metric. */
  name: string
  stroke: string
}

const GPU: Series[] = [
  { key: 'gpuAvg', label: 'Avg', name: 'average GPU usage', stroke: CHART_1 },
  { key: 'gpuMax', label: 'Max', name: 'peak GPU usage', stroke: CHART_2 },
]
const KV: Series[] = [
  { key: 'kvAvg', label: 'Avg', name: 'average KV cache usage', stroke: CHART_1 },
  { key: 'kvMax', label: 'Max', name: 'peak KV cache usage', stroke: CHART_2 },
]
const REQUESTS: Series[] = [
  { key: 'running', label: 'Running', name: 'requests running', stroke: CHART_1 },
  { key: 'waiting', label: 'Waiting', name: 'requests waiting', stroke: CHART_2 },
]
const SPEC_RATES: Series[] = [
  { key: 'acceptance', label: 'Acceptance', name: 'the acceptance rate', stroke: CHART_1 },
  { key: 'efficiency', label: 'Efficiency', name: 'efficiency', stroke: CHART_2 },
]
const SPEC_TOKENS: Series[] = [
  { key: 'acceptedTps', label: 'Accepted', name: 'accepted tokens per second', stroke: CHART_1 },
  { key: 'emittedTps', label: 'Emitted', name: 'emitted tokens per second', stroke: CHART_2 },
]

function measured(buckets: EngineBucket[], metric: EngineMetric): number[] {
  return buckets.flatMap((bucket) => {
    const value = bucket[metric]
    return value === null ? [] : [value]
  })
}

function legendItems(view: EngineView, series: Series[], format: (value: number | null) => string): LegendItem[] {
  return series
    .filter((line) => !isMissing(view, line.key))
    .map((line) => ({
      label: line.label,
      mark: <LineMark stroke={line.stroke} />,
      value: format(view.summary[line.key]),
    }))
}

/**
 * One chart of the section: the given lines over the window's buckets,
 * with a gap wherever a bucket has no value. Nothing when the window
 * measured none of its series; `Measured` says so instead.
 */
function EngineChart({
  view,
  drag,
  series,
  unit,
  format,
  formatTick,
  max,
  allowDecimals,
  lead,
  legend,
  alt,
}: {
  view: EngineView
  drag: DragZoom
  series: Series[]
  unit: string
  format: (value: number | null) => string
  formatTick: (value: number) => string
  max?: number
  allowDecimals?: boolean
  lead: React.ReactNode
  legend: React.ReactNode
  alt: string
}): React.JSX.Element | null {
  const drawn = series.filter((line) => !isMissing(view, line.key))
  if (drawn.length === 0) return null
  return (
    <Frame lead={lead} legend={legend} alt={alt} pan={drag.pan}>
      <BucketLineChart<EngineBucket>
        data={view.buckets}
        hours={view.hours}
        drag={drag}
        axis={<ValueAxis format={formatTick} max={max} allowDecimals={allowDecimals} />}
        unit={unit}
        lines={(bucket) => drawn.map((line) => ({ label: line.label, value: format(bucket[line.key]) }))}
      >
        {[...drawn].reverse().map((line) => (
          <SeriesLine key={line.key} dataKey={line.key} stroke={line.stroke} />
        ))}
      </BucketLineChart>
    </Frame>
  )
}

/**
 * A card's charts, then a note for each of its series Baseten did not send
 * in this window; one note instead of the charts when it sent none of them.
 */
function Measured({
  view,
  series,
  name,
  children,
}: {
  view: EngineView
  series: Series[]
  /** The words the note uses for the whole card. */
  name: string
  children: React.ReactNode
}): React.JSX.Element {
  const missing = series.filter((line) => isMissing(view, line.key))
  if (missing.length === series.length) return <Empty>{missingNote(name)}</Empty>
  return (
    <div className="space-y-8">
      {children}
      {missing.map((line) => (
        <Empty key={line.key}>{missingNote(line.name)}</Empty>
      ))}
    </div>
  )
}

/** An average and a max line on a 0 to 100 percent axis; the headline is the window's average. */
function PercentUsageChart({
  view,
  drag,
  series,
  headline,
  label,
  name,
}: {
  view: EngineView
  drag: DragZoom
  series: Series[]
  headline: EngineMetric
  label: string
  name: string
}): React.JSX.Element {
  const values = measured(view.buckets, headline)
  return (
    <Measured view={view} series={series} name={name}>
      <EngineChart
        view={view}
        drag={drag}
        series={series}
        unit="percent"
        format={formatPercent}
        formatTick={formatPercentTick}
        max={100}
        lead={<Headline value={formatPercent(view.summary[headline])} label={label} />}
        legend={<Legend items={legendItems(view, series, formatPercent)} />}
        alt={`${name} ranged from ${extent(values, formatPercent)} across this window's buckets.`}
      />
    </Measured>
  )
}

function RequestsChart({ view, drag }: { view: EngineView; drag: DragZoom }): React.JSX.Element {
  const running = measured(view.buckets, 'running')
  const items = legendItems(view, REQUESTS, formatCount)
  // The peak is the highest sample inside any bucket, not a second line, so it gets no mark.
  if (!isMissing(view, 'runningMax')) {
    items.splice(1, 0, { label: 'Peak running', mark: null, value: formatCount(view.summary.runningMax) })
  }
  return (
    <Measured view={view} series={REQUESTS} name="request counts">
      <EngineChart
        view={view}
        drag={drag}
        series={REQUESTS}
        unit="requests"
        format={formatCount}
        formatTick={formatCountTick}
        allowDecimals
        lead={<Headline value={formatCount(view.summary.running)} label="requests running" />}
        legend={<Legend items={items} />}
        alt={`Requests running ranged from ${extent(running, formatRate)} across this window's buckets.`}
      />
    </Measured>
  )
}

function formatTokensPerSecond(value: number | null): string {
  return value === null ? '—' : formatRate(value)
}

function SpecRateChart({ view, drag }: { view: EngineView; drag: DragZoom }): React.JSX.Element | null {
  const rates = measured(view.buckets, 'acceptance')
  return (
    <EngineChart
      view={view}
      drag={drag}
      series={SPEC_RATES}
      unit="percent"
      format={formatPercent}
      formatTick={formatPercentTick}
      max={100}
      lead={<Headline value={formatPercent(view.summary.acceptance)} label="acceptance rate" />}
      legend={<Legend items={legendItems(view, SPEC_RATES, formatPercent)} />}
      alt={`Acceptance rate ranged from ${extent(rates, formatPercent)} across this window's buckets.`}
    />
  )
}

function SpecTokenChart({ view, drag }: { view: EngineView; drag: DragZoom }): React.JSX.Element | null {
  const accepted = measured(view.buckets, 'acceptedTps')
  return (
    <EngineChart
      view={view}
      drag={drag}
      series={SPEC_TOKENS}
      unit="tokens / sec"
      format={formatTokensPerSecond}
      formatTick={formatCountTick}
      allowDecimals
      lead={<Caption>Tokens per second</Caption>}
      legend={<Legend items={legendItems(view, SPEC_TOKENS, formatTokensPerSecond)} />}
      alt={`Accepted tokens per second ranged from ${extent(accepted, formatRate)} across this window's buckets.`}
    />
  )
}

/** Whether the window measured at least one of the group's series, so its chart draws. */
function draws(view: EngineView, series: Series[]): boolean {
  return series.some((line) => !isMissing(view, line.key))
}

/**
 * The rate chart and the token chart side by side when both draw; a lone
 * chart takes the whole card instead of half of it.
 */
function SpecDecodeCharts({ view, drag }: { view: EngineView; drag: DragZoom }): React.JSX.Element {
  const charts = (
    <>
      <SpecRateChart view={view} drag={drag} />
      <SpecTokenChart view={view} drag={drag} />
    </>
  )
  return (
    <Measured view={view} series={[...SPEC_RATES, ...SPEC_TOKENS]} name="speculative decoding metrics">
      {draws(view, SPEC_RATES) && draws(view, SPEC_TOKENS) ? <SideBySide>{charts}</SideBySide> : charts}
    </Measured>
  )
}

function cardBodies(view: EngineView, drag: DragZoom): React.ReactNode[] {
  if (!hasSamples(view)) return CARDS.map(() => <Empty>{NO_SAMPLES}</Empty>)
  return [
    <PercentUsageChart view={view} drag={drag} series={GPU} headline="gpuAvg" label="gpu usage" name="GPU usage" />,
    <PercentUsageChart view={view} drag={drag} series={KV} headline="kvAvg" label="kv cache usage" name="KV cache usage" />,
    <RequestsChart view={view} drag={drag} />,
    <SpecDecodeCharts view={view} drag={drag} />,
  ]
}

/** The four cards, each with the same body or its own: two usage cards in one row, then two full-row cards. */
function Cards({ bodies }: { bodies: React.ReactNode[] }): React.JSX.Element {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {CARDS.map((card, index) => (
        <MetricsCard key={card.title} title={card.title} subtitle={card.subtitle} wide={card.wide}>
          {bodies[index]}
        </MetricsCard>
      ))}
    </div>
  )
}

function EngineBody({
  modelId,
  hours,
  end,
  shown,
  onZoom,
  pan,
  isBaseten,
  routeLoaded,
  routeFailed,
}: EngineProps): React.JSX.Element {
  // The read only takes hours back from now, so a stretch moved back
  // (TRA-1592) loads a window long enough to reach it and draws its part.
  const metrics = useModelEngineMetrics(modelId, isBaseten, engineHoursToLoad(end, hours))
  if (!routeLoaded) return <Skeleton className="h-28 w-full" />
  if (routeFailed) return <p className="text-sm text-destructive">{ROUTE_FAILED}</p>
  if (!isBaseten) return <Empty>{NOT_ON_FLEET}</Empty>
  if (metrics.isError) {
    return <Cards bodies={CARDS.map(() => <p className="text-sm text-destructive">{LOAD_FAILED}</p>)} />
  }
  if (metrics.data === undefined) {
    return <Cards bodies={CARDS.map(() => <Skeleton className="h-44 w-full" />)} />
  }
  const view = toEngineView(metrics.data, shown)
  if (view === null) return <Empty>{NO_ENGINE_DATA_YET}</Empty>
  const drag: DragZoom = { bucketMs: metrics.data.bucket_seconds * 1000, onZoom, pan }
  return (
    <div className="space-y-4">
      {/* The previous window stays up while a longer one loads. */}
      {metrics.isPlaceholderData && <Note>Loading...</Note>}
      {view.collectedSince !== null && <Empty>{view.collectedSince}</Empty>}
      <Cards bodies={cardBodies(view, drag)} />
    </div>
  )
}

interface EngineProps {
  modelId: string
  hours: number
  /** Where the window ends once the page moved it back; null at now. */
  end: number | null
  /** The stretch every card on the page shows: the zoom, or the window ending at `end` or now. */
  shown: TimeRange
  /** A drag across a chart: the page's new zoom. */
  onZoom: DragZoom['onZoom']
  /** The page's arrows and Reset zoom, drawn on every chart. */
  pan: DragZoom['pan']
  isBaseten: boolean
  /** The route lookup answered, with a route or an error. */
  routeLoaded: boolean
  /** The route lookup failed, so whether the model is on our fleet is unknown. */
  routeFailed: boolean
}

export function Engine(props: EngineProps): React.JSX.Element {
  return (
    <section className="border-t border-border py-6">
      <SectionHeading title="Engine" subtitle="From vLLM on Baseten, sampled every 2 minutes" />
      <div className="mt-5">
        <EngineBody {...props} />
      </div>
    </section>
  )
}
